import { useFetcher } from "@remix-run/react";
import {
  MapPin,
  Star,
  Car,
  Zap,
  ExternalLink,
  TriangleAlert,
  BatteryCharging,
  X,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  MoveHorizontal,
  Mountain,
  Clock,
  Leaf,
  Battery,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Card } from "#app/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "#app/components/ui/dialog.tsx";
import { Skeleton } from "#app/components/ui/skeleton.tsx";
import { Button } from "./ui/button";

// Add a constant at the top level for CO2 salvos per km
const CO2_salvos_PER_KM = 0.2; // kg CO2 salvos per kilometer

// Add this before the Trip component
function convertTime(time: number): string {
  const hours = Math.floor(time);
  const minutes = Math.round((time - hours) * 60);
  return `${hours}h ${minutes}m`;
}

export default function Trip({
  routeStations,
  routeTotalTime,
  routeTotalDistance,
  routeTotalChargingTime,
  routeTotalDrivingTime,
  routeId,
  setRouteStations,
  setRouteFeatures,
  resetRoute,
  directionsRef,
  mapRef,
  blockedStations,
  setBlockedStations,
  routeFetcherState,
}: {
  routeStations: any[];
  routeTotalTime: number;
  routeTotalDistance: number;
  routeTotalChargingTime: number;
  routeTotalDrivingTime: number;
  routeId: string;
  setRouteStations: (stations: any[]) => void;
  setRouteFeatures: (features: any[]) => void;
  resetRoute: () => void;
  directionsRef: any;
  mapRef: any;
  blockedStations: any[];
  setBlockedStations: (stations: any[]) => void;
  routeFetcherState: string;
}) {
  function formatTime(date: Date): string {
    return date.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  function generateGoogleMapsUrl(stations: any[]): string {
    const waypoints = stations
      .slice(1, -1)
      .map((s) =>
        s.point === "Station"
          ? `${s.station.latitude},${s.station.longitude}`
          : `${s.coords[0]},${s.coords[1]}`,
      )
      .join("|");
    const start = stations[0];
    const end = stations[stations.length - 1];
    return `https://www.google.com/maps/dir/?api=1&origin=${start.coords[0]},${start.coords[1]}&destination=${end.coords[0]},${end.coords[1]}&waypoints=${waypoints}&travelmode=driving`;
  }

  const startTime = new Date();
  let currentTime = startTime;

  // NEW: compute total CO2 salvos based on total distance
  const totalCO2salvos = routeTotalDistance * CO2_salvos_PER_KM;

  const ratingFetcher = useFetcher();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const initialRatingRef = useRef<number | null>(null);
  const previousRouteIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (routeId !== previousRouteIdRef.current) {
      // Route ID has changed, reset rating to 0
      setRating(0);
      initialRatingRef.current = 0;
      previousRouteIdRef.current = routeId;
    } else {
      // Load the salvos rating from localStorage when the component mounts
      const salvosRating = localStorage.getItem(`route-rating-${routeId}`);
      if (salvosRating) {
        const parsedRating = parseInt(salvosRating, 10);
        setRating(parsedRating);
        initialRatingRef.current = parsedRating;
      } else {
        initialRatingRef.current = 0;
      }
    }
  }, [routeId]);

  useEffect(() => {
    if (rating > 0 && rating !== initialRatingRef.current) {
      // Save the rating to localStorage
      localStorage.setItem(`route-rating-${routeId}`, rating.toString());

      const formData = new FormData();
      formData.append("rating", rating.toString());
      formData.append("routeId", routeId);
      formData.append("intent", "rating");
      ratingFetcher.submit(formData, {
        method: "post",
        action: "/mapa",
      });

      // Update the initial rating ref to prevent further API calls
      initialRatingRef.current = rating;
    }
  }, [rating, routeId, ratingFetcher]);

  return (
    <div className="mx-auto w-full max-w-md space-y-6">
      {/* Route Summary Card */}
      <Card className="overflow-hidden border-none shadow-lg">
        <div className="bg-gradient-to-r from-blue-800 to-blue-900 p-5 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-white/90 p-2">
                <img
                  className="h-10 w-10"
                  src="/img/logo.webp"
                  alt="Logo Carregados"
                />
              </div>
              <h2 className="text-2xl font-bold tracking-tight">
                {convertTime(routeTotalTime)}
              </h2>
            </div>
            <div className="rounded-full bg-white/20 px-3 py-1 text-sm font-medium backdrop-blur-sm">
              {Math.round(routeTotalDistance)} km
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 bg-white p-4">
          <div className="flex flex-col items-center rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
            <div className="mb-1 flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-blue-600">
              <Car className="h-4 w-4" />
            </div>
            <span className="text-xs font-medium text-gray-500">Dirigindo</span>
            <span className="font-medium">
              {convertTime(routeTotalDrivingTime)}
            </span>
          </div>

          <div className="flex flex-col items-center rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
            <div className="mb-1 flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 text-amber-600">
              <Zap className="h-4 w-4" />
            </div>
            <span className="text-xs font-medium text-gray-500">
              Carregando
            </span>
            <span className="font-medium">
              {convertTime(routeTotalChargingTime)}
            </span>
          </div>

          <div className="flex flex-col items-center rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
            <div className="mb-1 flex h-8 w-8 items-center justify-center rounded-full bg-green-100 text-green-600">
              <Leaf className="h-4 w-4" />
            </div>
            <span className="text-xs font-medium text-gray-500">
              CO₂ Salvos
            </span>
            <span className="font-medium">
              {(routeTotalDistance * CO2_salvos_PER_KM).toFixed(1)} kg
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between border-t bg-gray-50 px-4 py-2 text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            <span>
              Horário estimado de chegada:{" "}
              {formatTime(
                new Date(startTime.getTime() + routeTotalTime * 3600000),
              )}
            </span>
          </div>
        </div>
      </Card>

      {/* Charging Stops */}
      <div className="relative space-y-4">
        {/* Vertical connecting line */}
        <div className="absolute bottom-[52px] left-7 top-[52px] w-0.5 bg-gray-400" />

        {routeStations.map((station, index) => {
          const timeToNext =
            index < routeStations.length - 1
              ? routeStations[index + 1].time_from_previous
              : undefined;

          // Add time from previous station
          currentTime = new Date(
            currentTime.getTime() + station.time_from_previous * 3600000,
          );
          // Add charging time if it's a charging station
          if (station.point === "Station") {
            currentTime = new Date(
              currentTime.getTime() + station.charging_time * 3600000,
            );
          }

          return (
            <React.Fragment
              key={
                station.point === "Station"
                  ? station.station.id
                  : `${station.point.toLowerCase()}-${index}`
              }
            >
              {/* Show route segment info if not the last station */}
              {index > 0 && (
                <RouteSegmentInfo
                  distance={station.distance_from_previous}
                  time={station.time_from_previous}
                  currentElevation={routeStations[index - 1].elevation}
                  nextElevation={station.elevation}
                />
              )}

              {station.point === "Station" ? (
                <ChargingStopCard
                  key={index}
                  time={formatTime(currentTime)}
                  station={station}
                  nextStation={routeStations[index + 1]}
                  isBlocked={blockedStations.some(
                    (s) => s.stationId === station.station.id,
                  )}
                  onBlock={() => {
                    setBlockedStations([
                      ...blockedStations,
                      {
                        stationId: station.station.id,
                        stationName: station.station.name,
                      },
                    ]);
                  }}
                  isLoading={routeFetcherState !== "idle"}
                  blockedStations={blockedStations}
                />
              ) : (
                <Card
                  key={index}
                  className={`relative p-4 ${station.point === "Start" ? "bg-gradient-to-r from-blue-800 to-blue-900 text-white" : station.point === "End" ? "bg-gradient-to-r from-blue-900 to-blue-800 text-white" : ""}`}
                >
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-2">
                      <div>
                        <h3 className="font-medium">
                          {station.point === "Start" ? "Partida" : "Chegada"}
                        </h3>
                        <p className="text-sm">{formatTime(currentTime)}</p>
                      </div>
                    </div>

                    <div className="w-full space-y-4">
                      <div>
                        <div className="mb-1 flex items-center gap-2">
                          <Battery className="h-4 w-4" />
                          <span className="text-sm">
                            {station.point === "Start"
                              ? `${Math.round(station.soc_departure)}%`
                              : `${Math.round(station.soc_arrival)}%`}
                          </span>
                        </div>
                        <div className="h-2 overflow-hidden rounded-full bg-white/20">
                          <div
                            className="h-full rounded-full bg-green-400"
                            style={{
                              width: `${
                                station.point === "Start"
                                  ? station.soc_departure
                                  : station.soc_arrival
                              }%`,
                            }}
                          />
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Mountain className="h-4 w-4" />
                        <span className="text-sm">
                          {Math.round(station.elevation)}m elevação
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Action Buttons and Info Sectionss */}
      <div className="mx-auto flex w-full max-w-md flex-col gap-2">
        {blockedStations.length > 0 && (
          <BlockedStations
            blockedStations={blockedStations}
            setBlockedStations={setBlockedStations}
          />
        )}
        <Button
          variant={"secondary"}
          onClick={() =>
            window.open(generateGoogleMapsUrl(routeStations), "_blank")
          }
        >
          <ExternalLink className="mr-2 h-4 w-4" />
          Abrir Rota no Google Maps
        </Button>
        <Button
          variant={"destructive"}
          onClick={() => {
            resetRoute();
          }}
        >
          Reiniciar
        </Button>

        <ratingFetcher.Form
          method="post"
          className="mt-4 flex flex-col items-center rounded-lg bg-gray-100 py-1 shadow-md"
        >
          <label
            htmlFor="routeRating"
            className="mb-2 text-sm font-medium text-gray-700"
          >
            Como você avalia esta rota?
          </label>
          <div className="flex text-yellow-400">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                className={`text-2xl focus:outline-none ${
                  star <= (hoverRating || rating) ? "text-yellow-500" : ""
                }`}
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
              >
                {star <= (hoverRating || rating) ? "★" : "☆"}
              </button>
            ))}
          </div>
          {rating > 0 && (
            <p className="mt-2 text-sm text-primary">
              Obrigado pela sua avaliação!
            </p>
          )}
        </ratingFetcher.Form>
      </div>
    </div>
  );
}

function BlockedStations({
  blockedStations,
  setBlockedStations,
}: {
  blockedStations: any[];
  setBlockedStations: (stations: any[]) => void;
}) {
  return (
    <div>
      <h2 className="text-sm font-bold">Estações Bloqueadas:</h2>
      <div className="text-sm text-muted-foreground">
        {blockedStations
          .map((station) => station.stationName)
          .join(", ")
          .replace(/,([^,]*)$/, " e$1")}
      </div>
    </div>
  );
}

function RouteSegmentInfo({
  distance,
  time,
  currentElevation,
  nextElevation,
}: {
  distance: number;
  time: number;
  currentElevation: number;
  nextElevation: number;
}) {
  const elevationDiff = nextElevation - currentElevation;

  return (
    <div className="-my-2 ml-14 flex items-center gap-4 py-2 text-sm text-muted-foreground">
      <div className="flex items-center gap-1">
        <Car className="h-4 w-4" />
        <span>{Math.round(distance)} km</span>
      </div>
      <div className="flex items-center gap-1">
        <Clock className="h-4 w-4" />
        <span>{convertTime(time)}</span>
      </div>
      <div className="flex items-center gap-1">
        <Mountain className="h-4 w-4" />
        <span>
          {elevationDiff > 0 ? "+" : ""}
          {Math.round(elevationDiff)}m
        </span>
      </div>
    </div>
  );
}

function ChargingStopCard({
  time,
  station,
  nextStation,
  isBlocked,
  onBlock,
  isLoading,
  blockedStations,
}: {
  time: string;
  station: any;
  nextStation: any;
  isBlocked: boolean;
  onBlock: () => void;
  isLoading: boolean;
  blockedStations: any[];
}) {
  const [dialogOpen, setDialogOpen] = useState(false);

  if (isBlocked && isLoading) {
    return <Skeleton className="h-[200px] w-full" />;
  }

  return (
    <Card className="relative border-gray-400 p-4">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h3 className="text-base font-medium">{station.station.name}</h3>
          <p className="text-sm text-muted-foreground">{time}</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex gap-0.5">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`h-3.5 w-3.5 ${
                  i < Math.round(station.station.rating * 5)
                    ? "fill-yellow-400 text-yellow-400"
                    : "text-gray-300"
                }`}
              />
            ))}
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <button className="rounded-full bg-red-500 p-0.5 text-white hover:bg-red-600">
                <X className="h-4 w-4" />
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Remover Estação</DialogTitle>
                <DialogDescription>
                  Ao confirmar, a melhor rota será calculada sem considerar essa
                  estação.
                </DialogDescription>
              </DialogHeader>
              <div className="flex justify-center space-x-2">
                <Button
                  variant="destructive"
                  onClick={() => {
                    if (!blockedStations.includes(station.station.id)) {
                      onBlock();
                    }
                    setDialogOpen(false);
                  }}
                >
                  Remover
                </Button>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-8 gap-y-2">
        <div className="col-span-2 mb-2">
          <div className="mb-1 flex items-center gap-2">
            <Battery className="h-4 w-4 shrink-0 text-green-500" />
            <span className="text-sm">
              {Math.round(station.soc_arrival)}% →{" "}
              {Math.round(station.soc_departure)}%
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-gray-200">
            <div className="flex h-full rounded-full">
              <div
                className="h-full bg-green-600"
                style={{ width: `${station.soc_arrival}%` }}
              />
              <div
                className="h-full bg-green-300"
                style={{
                  width: `${station.soc_departure - station.soc_arrival}%`,
                }}
              />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 shrink-0 text-amber-500" />
          <span className="text-sm">
            {Math.round(station.energy_charged_kwh)} kWh
          </span>
        </div>

        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 shrink-0 text-purple-500" />
          <span className="text-sm">{convertTime(station.charging_time)}</span>
        </div>

        <div className="flex items-center gap-2">
          <Mountain className="h-4 w-4 shrink-0 text-blue-500" />
          <span className="text-sm">
            {Math.round(station.elevation)}m elevação
          </span>
        </div>

        <div className="flex items-center gap-2">
          <Leaf className="h-4 w-4 shrink-0 text-green-500" />
          <span className="text-sm">
            {(nextStation?.distance_from_previous * CO2_salvos_PER_KM).toFixed(
              2,
            )}
            kg CO₂ salvos
          </span>
        </div>
      </div>
    </Card>
  );
}
